export type CategoryId = string;

export interface Category {
  id: CategoryId;
  label: string;
  emoji: string;
  order: number;
}

export interface QuickAdd {
  label: string;
  amount: number;
  cat: CategoryId;
}

export interface User {
  username: string;
  email: string;
  budget: number;
  quickAdds: QuickAdd[];
  categories: Category[];
  createdAt: string;
}

export interface Expense {
  id: string;
  username: string;
  amount: number;
  cat: CategoryId;
  note: string;
  date: string;
}

export interface NewExpense {
  amount: number;
  cat: CategoryId;
  note?: string;
  date?: string;
}

export interface SignupInput {
  email: string;
  password: string;
  username: string;
  budget: number;
}

export interface LoginInput {
  email: string;
  password: string;
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

const API_BASE = process.env.BUN_PUBLIC_API_URL ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? res.statusText;
    throw new ApiError(code, message, res.status);
  }
  return body as T;
}

export const api = {
  signup: (input: SignupInput) =>
    request<User>("/signup", { method: "POST", body: JSON.stringify(input) }),

  login: (input: LoginInput) =>
    request<User>("/login", { method: "POST", body: JSON.stringify(input) }),

  logout: () => request<void>("/logout", { method: "POST" }),

  me: () => request<User>("/me"),

  updateMe: (
    patch: Partial<Pick<User, "username" | "budget" | "quickAdds" | "categories">>,
  ) =>
    request<User>("/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteMe: () => request<void>("/me", { method: "DELETE" }),

  listExpenses: (month?: string) => {
    const q = month ? `?month=${month}` : "";
    return request<{ data: Expense[] }>(`/expenses${q}`).then((r) => r.data);
  },

  addExpense: (exp: NewExpense) =>
    request<Expense>("/expenses", {
      method: "POST",
      body: JSON.stringify(exp),
    }),

  deleteExpense: (id: string) =>
    request<void>(`/expenses/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
